import type { AssetStore } from '../assets/asset-store';
import { backgroundStore } from '../assets/background-store';
import type { WorldSetupState } from '../game/world';
import type { Camera } from './camera';
import { NEBULA_PARALLAX, nebulaSeed, nebulaSprites } from './nebulae';

const NEAR_DIVISOR = 8;
const FAR_DIVISOR = 20;
const FAR_SCALE = 0.73;
const FAR_ALPHA = 0.5;
const FADE_MS = 700;

interface Plane {
  readonly element: HTMLDivElement;
  readonly divisor: number;
  readonly scale: number;
  readonly alpha: number;
  period: number;
  source: string;
}

export class BackgroundLayer {
  private readonly host: HTMLDivElement;
  private readonly nebulaHost: HTMLDivElement;
  private readonly planes: Plane[] = [];
  private startedAt: number | null = null;
  private fadeValue = 0;
  private nebulaKey: number | null = null;
  private nebulaTransform = '';
  private readonly trails: HTMLDivElement;

  constructor(
    canvas: HTMLCanvasElement,
    private readonly assets: AssetStore,
  ) {
    this.host = document.createElement('div');
    this.host.style.cssText =
      'position:fixed;inset:0;overflow:hidden;background:#04060f;pointer-events:none;contain:strict';
    this.nebulaHost = document.createElement('div');
    this.nebulaHost.style.cssText = 'position:absolute;left:0;top:0;transform-origin:0 0';
    this.planes.push(this.plane(FAR_DIVISOR, FAR_SCALE, FAR_ALPHA));
    this.planes.push(this.plane(NEAR_DIVISOR, 1, 1));
    this.host.append(this.nebulaHost);
    canvas.parentNode?.insertBefore(this.host, canvas);
    this.trails = document.createElement('div');
    this.trails.style.cssText =
      'position:fixed;inset:0;overflow:hidden;pointer-events:none;contain:strict';
    canvas.parentNode?.insertBefore(this.trails, canvas);
  }

  get settled(): boolean {
    return this.fadeValue >= 1;
  }

  get trailHost(): HTMLElement {
    return this.trails;
  }

  dispose(): void {
    this.host.remove();
    this.trails.remove();
  }

  sync(camera: Camera, world: WorldSetupState, now: number): void {
    const source = this.assets.backgroundImage;
    if (source === null) {
      this.startedAt = null;
      this.fadeValue = 0;
      this.host.style.opacity = '0';
      return;
    }
    this.startedAt ??= now;
    this.fadeValue = Math.min(1, (now - this.startedAt) / FADE_MS);
    this.host.style.opacity = String(this.fadeValue);

    const v3 = backgroundStore.style === 'v3';
    const ratio = camera.pixelRatio;
    const cssWidth = camera.deviceWidth / ratio;
    const cssHeight = camera.deviceHeight / ratio;
    for (const [index, plane] of this.planes.entries()) {
      const far = index === 0;
      plane.element.style.display = far && !v3 ? 'none' : 'block';
      if (far && !v3) continue;
      this.syncPlane(plane, camera, source, cssWidth, cssHeight);
    }
    this.syncNebulae(camera, world, v3, ratio);
  }

  private plane(divisor: number, scale: number, alpha: number): Plane {
    const element = document.createElement('div');
    element.style.cssText = `position:absolute;left:0;top:0;background-repeat:repeat;will-change:transform;opacity:${String(alpha)}`;
    this.host.append(element);
    return { element, divisor, scale, alpha, period: 0, source: '' };
  }

  private syncPlane(
    plane: Plane,
    camera: Camera,
    source: HTMLImageElement,
    cssWidth: number,
    cssHeight: number,
  ): void {
    const tile = source.naturalWidth;
    if (tile === 0) return;
    const period = (camera.worldScale * plane.scale * tile) / camera.pixelRatio;
    if (plane.source !== source.src) {
      plane.source = source.src;
      plane.element.style.backgroundImage = `url(${source.src})`;
    }
    if (plane.period !== period) {
      plane.period = period;
      plane.element.style.backgroundSize = `${String(period)}px ${String(period)}px`;
      plane.element.style.width = `${String(Math.ceil(cssWidth + period))}px`;
      plane.element.style.height = `${String(Math.ceil(cssHeight + period))}px`;
    }
    const x = planeShift(
      camera.offsetX,
      camera.worldScale,
      plane.divisor,
      camera.pixelRatio,
      period,
    );
    const y = planeShift(
      camera.offsetY,
      camera.worldScale,
      plane.divisor,
      camera.pixelRatio,
      period,
    );
    plane.element.style.transform = `translate3d(${String(x)}px,${String(y)}px,0)`;
  }

  private syncNebulae(camera: Camera, world: WorldSetupState, v3: boolean, ratio: number): void {
    this.nebulaHost.style.display = v3 ? 'block' : 'none';
    if (!v3) return;
    const seed = nebulaSeed(world);
    if (seed !== this.nebulaKey) {
      this.nebulaKey = seed;
      this.nebulaHost.replaceChildren();
      for (const { spec, sprite } of nebulaSprites(world)) {
        const cloud = document.createElement('div');
        cloud.style.cssText = `position:absolute;left:${String(spec.x - spec.radius)}px;top:${String(spec.y - spec.radius)}px;width:${String(spec.radius * 2)}px;height:${String(spec.radius * 2)}px;background-size:100% 100%;opacity:${String(Math.min(1, spec.alpha))};mix-blend-mode:plus-lighter;background-image:url(${sprite.toDataURL()})`;
        this.nebulaHost.append(cloud);
      }
    }
    const scale = camera.worldScale / ratio;
    const x = (-camera.offsetX * camera.worldScale * NEBULA_PARALLAX) / ratio;
    const y = (-camera.offsetY * camera.worldScale * NEBULA_PARALLAX) / ratio;
    const transform = `translate3d(${String(x)}px,${String(y)}px,0) scale(${String(scale)})`;
    if (transform !== this.nebulaTransform) {
      this.nebulaTransform = transform;
      this.nebulaHost.style.transform = transform;
    }
  }
}

/** Mirrors what the canvas pattern fill puts under the same camera: the plane lands at `-offset/divisor` in device pixels, wrapped back by whole tiles so the element never has to be larger than a screen and a tile. */
export function planeShift(
  worldOffset: number,
  worldScale: number,
  divisor: number,
  ratio: number,
  period: number,
): number {
  if (period <= 0) return 0;
  const shift = (-worldOffset * worldScale) / (divisor * ratio);
  const wrapped = shift % period;
  return wrapped > 0 ? wrapped - period : wrapped;
}
